/**
 * PATH: packages/bills/src/components/BillDataPanel.tsx
 * FILE_ID: BILLS-PKG-CMP-DATA-001
 * PURPOSE: Right rail — show / edit / action the selected bill's data.
 *
 * WHY: After viewing the source document, the bookkeeper edits the
 *      extracted fields and either approves, rejects, or pushes to
 *      Xero. This panel is the single place those mutations originate
 *      so optimistic updates stay consistent.
 *
 * UX rules:
 *   - Edits apply to a local draft. When the embedder wires ``onAutoSave``
 *     (BookkeepingWorkspace does) the DB FOLLOWS THE DRAFT — a debounced
 *     PATCH fires ~1.2s after the last change, including PDF picks and
 *     undo/redo. Without it, edits stay local until "Save" is pressed.
 *   - "Save" is disabled when nothing has changed.
 *   - Approve / Reject / Push-to-Xero respect the bill's current status
 *     (e.g. you cannot push to Xero a bill already pushed).
 *
 * STRUCTURE: this file owns the mutation orchestration (draft/undo history,
 * supplier auto-create, save/approve/reject/push handlers) and composes the
 * presentational pieces that were split out (P4-1) into sibling modules:
 *   - BillDataPanelHeader / ReclassifyMenu — pinned identity + action row
 *   - BillVendorSection / BillIdentitySection / BillAmountsSection — form body
 *   - BillFieldPrimitives (Field/Section/Empty + capture context),
 *     BillPanelChrome (StatusBadge/ReceivedMetaRow/BillFormSkeleton),
 *     LineItemsSection — shared presentational primitives
 *   - bill-document-type / format-datetime / merge-bill / bill-panel-styles —
 *     pure helpers + style constants
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useBillsClient } from '../client';
import { useDraftHistory } from './draftHistory';
import type {
  BillDetail,
  BillEditablePatch,
  BillLineItem,
  BillSupplierOption,
  CoaAccount,
  MarkBillPaidInput,
  SetSupplierTermInput,
  BillTermType,
} from '../types';
import { BillAllocationSection } from './BillAllocation';
import { BillLineAllocationGrid } from './BillLineAllocationGrid';
import { BillJournalPreview } from './BillJournalPreview';
import { BillPaymentSection } from './BillPaymentSection';
import { AlertTriangle } from 'lucide-react';
import { parseLineFieldKey, type BillFieldKey } from './billFieldKeys';
import { coerceCapturedValue } from './captureValue';
import { useFieldHighlight } from './useFieldHighlight';
import { mergeBill } from './merge-bill';
import { getDocumentType, documentTypeLabel, needsManualReview } from './bill-document-type';
import { Empty } from './BillFieldPrimitives';
import { BillFormSkeleton } from './BillPanelChrome';
import { BillDataPanelHeader } from './BillDataPanelHeader';
import { BillVendorSection } from './BillVendorSection';
import { BillIdentitySection } from './BillIdentitySection';
import { BillAmountsSection } from './BillAmountsSection';
import { LineItemsSection } from './LineItemsSection';

interface Props {
  bill: BillDetail | null;
  loading: boolean;
  busy: boolean;
  mutationError: string | null;
  onSave: (patch: BillEditablePatch) => Promise<void>;
  onApprove: () => Promise<void>;
  onReject: (reason: string) => Promise<void>;
  onPushToXero: () => Promise<void>;
  /**
   * Re-run AI extraction on the source email. Optional so older
   * embedders that haven't wired the action up still compile; when
   * absent, the Re-extract button is hidden. New consumers SHOULD
   * pass it — see BookkeepingWorkspace.handleReExtract for the
   * canonical wiring.
   */
  onReExtract?: () => Promise<void>;
  /**
   * Reclassify the bill's email out of the AP queue. Called when the
   * user picks a category from the "Not a bill?" dropdown. The
   * category string must be a value from RECLASSIFY_OPTIONS (which
   * mirrors the backend's EMAIL_CATEGORIES). Hidden when undefined.
   */
  onReclassify?: (category: string) => Promise<void>;
  /**
   * Set the linked supplier's payment term + re-derive the due date.
   * Optional — when absent the Payment section's term selector is hidden.
   */
  onSetSupplierTerm?: (input: SetSupplierTermInput) => Promise<void>;
  /**
   * Mark the bill as already paid / unpaid. Optional — when absent the
   * Payment section's "Mark as paid" affordance is hidden.
   */
  onMarkPaid?: (input: MarkBillPaidInput) => Promise<void>;
  /**
   * Cross-highlight (p1c): the field currently selected in the Read pane.
   * When set, the matching input is scrolled to + ring-highlighted.
   */
  activeField?: BillFieldKey | null;
  /**
   * Report which field the user focused/clicked in THIS form, so the Read
   * pane can highlight the same cell. Optional — absent in embedders that
   * don't show the read view.
   */
  onFieldSelect?: (key: BillFieldKey | null) => void;
  /**
   * "Pick from PDF" (p1e). A value picked off the document, addressed to a
   * field key. Applied (type-coerced) to the draft once per ``nonce`` change.
   */
  captured?: { field: BillFieldKey; value: string; nonce: number } | null;
  /**
   * Autosave sink (2026-07-24 — "edit in PDF → sidebar + DB, and vice
   * versa"). When provided, the DB FOLLOWS THE DRAFT: every draft change
   * (typing, a PDF pick, undo/redo) is PATCHed ~1.2s after the last change.
   * Unlike ``onSave``, the caller must NOT replace the detail with the
   * response — the panel keeps the pre-edit bill as the undo baseline, so
   * ⌘Z still walks back to the original values (and the reverts are
   * autosaved too). Absent → the classic explicit-Save-only behaviour.
   */
  onAutoSave?: (patch: BillEditablePatch) => Promise<void>;
  /**
   * Live merged (bill + draft) preview for the document pane, so the PDF's
   * derived highlight regions track UNSAVED edits, not just server state.
   * Reports null while the draft is clean (lets a hidden duplicate mount —
   * the narrow-screen drawer — never clobber the active one's preview).
   */
  onDraftPreview?: (preview: Partial<BillDetail> | null) => void;
}

/** Idle gap after the last draft change before the autosave PATCH fires. */
const AUTOSAVE_DEBOUNCE_MS = 1200;

export function BillDataPanel({
  bill,
  loading,
  busy,
  mutationError,
  onSave,
  onApprove,
  onReject,
  onPushToXero,
  onReExtract,
  onReclassify,
  onSetSupplierTerm,
  onMarkPaid,
  activeField,
  onFieldSelect,
  captured,
  onAutoSave,
  onDraftPreview,
}: Props) {
  const client = useBillsClient();
  const asideRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Allow dismissing the mutation-error banner. We remember WHICH message was
  // dismissed (not just a boolean) so a fresh, different failure re-surfaces
  // the banner instead of staying silently hidden.
  const [dismissedError, setDismissedError] = useState<string | null>(null);
  const showMutationError = !!mutationError && mutationError !== dismissedError;
  // Cross-highlight: ring the input matching the Read pane's selection.
  useFieldHighlight(bodyRef, activeField ?? null);
  // Undo/redo over the edit draft. Controlled inputs kill the browser's native
  // ⌘Z, so a wrong "Pick from PDF" was unrecoverable without retyping — this
  // makes every draft change reversible. `setDraft`/`resetDraft` keep the rest
  // of the panel's call sites unchanged; `update` carries a coalescing label
  // so typing in one field is a single undo step while a pick is its own step.
  const history = useDraftHistory<BillEditablePatch>({});
  const draft = history.present;
  const resetDraft = history.reset;
  const historyUpdate = history.update;
  const undoDraft = history.undo;
  const redoDraft = history.redo;
  const { canUndo, canRedo } = history;
  const setDraft = useCallback(
    (
      updater: ((prev: BillEditablePatch) => BillEditablePatch) | BillEditablePatch,
      label: string | null = null,
      coalesce = false,
    ) =>
      historyUpdate(
        typeof updater === 'function'
          ? (updater as (prev: BillEditablePatch) => BillEditablePatch)
          : () => updater,
        label,
        coalesce,
      ),
    [historyUpdate],
  );
  // Supplier auto-create / term-apply orchestration state. Shared by the
  // one-click "Create & link supplier" button and the payment-term chips
  // (which auto-create the supplier when none is linked yet).
  const [supplierBusy, setSupplierBusy] = useState(false);
  const [supplierError, setSupplierError] = useState<string | null>(null);
  // Latching reveal flag for the optional Extra Charge carve-out
  // (Freight / Shipping / Handling). Once the bookkeeper has clicked
  // "+ Add extra charge" we keep the inputs visible even if the
  // amount is briefly cleared mid-edit — otherwise typing 0 or
  // backspacing the value would make the row vanish under the
  // user's cursor. Reset alongside ``draft`` whenever the selected
  // bill (or its extracted_at) changes; auto-treated as ``true``
  // implicitly when the bill arrives with a positive
  // ``extra_charge_amount`` (see ``hasExtraCharge`` below).
  const [extraChargeRevealed, setExtraChargeRevealed] = useState(false);
  // Org Chart of Accounts for the per-line allocation grid's CoA picker
  // (p3b). Fetched once on mount via the injected client (org-stable, not
  // per-bill). Optional client method → grid degrades to free-text codes.
  const [coaAccounts, setCoaAccounts] = useState<CoaAccount[] | null>(null);
  const [coaLoading, setCoaLoading] = useState(false);

  useEffect(() => {
    if (!client.getChartOfAccounts) return;
    let alive = true;
    setCoaLoading(true);
    client
      .getChartOfAccounts()
      .then((resp) => {
        if (alive) setCoaAccounts(resp.accounts ?? []);
      })
      .catch(() => {
        // CoA is a convenience for the picker — a fetch failure just
        // degrades the account cell to free-text, never blocks coding.
        if (alive) setCoaAccounts([]);
      })
      .finally(() => {
        if (alive) setCoaLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [client]);

  // ── Autosave: the DB follows the draft ────────────────────────────────
  // Debounced PATCH of the draft whenever it settles. Undo can REMOVE a key
  // whose value was already autosaved; for those keys the patch writes the
  // pre-edit baseline (the ``bill`` prop, which the caller keeps stable
  // across autosaves) back, so the DB follows reverts too.
  const [autoSaving, setAutoSaving] = useState(false);
  const autoSavedKeysRef = useRef<Set<string>>(new Set());
  const lastAutoSavedDraftRef = useRef<BillEditablePatch | null>(null);

  // Reset draft + close any open submenu when the selected bill
  // changes — OR when the same bill gets re-extracted. Using
  // `bill?.id` alone misses re-extract because the AI overwrites
  // the existing row in place (id is stable, only the extracted
  // fields change). Including `extracted_at` makes the panel
  // discard any stale draft the user typed before re-extracting,
  // which is the only sane behaviour: the new AI output IS the
  // intended starting point. Applies to every field uniformly
  // (Bill #, dates, totals, line items) — no field-specific
  // clearing is needed.
  //
  // MUST run before the captured-apply effect below: on remount (e.g. the
  // details drawer opening a second BillDataPanel) both effects fire, and a
  // reset that lands AFTER a pick would wipe the PDF→sidebar write while
  // still stamping lastCaptureNonce so the pick never re-applies.
  const lastCaptureNonce = useRef<number>(0);
  useEffect(() => {
    resetDraft({});
    lastCaptureNonce.current = 0;
    setExtraChargeRevealed(false);
    setSupplierError(null);
    autoSavedKeysRef.current = new Set();
    lastAutoSavedDraftRef.current = null;
  }, [bill?.id, bill?.extracted_at, resetDraft]);

  // "Pick from PDF" apply (p1e): when a new value is picked off the document
  // (nonce bumps), coerce it for the target field's type and write it into
  // the draft. Functional setDraft reads the latest merged state so we never
  // clobber a concurrent edit. Idempotent per nonce.
  useEffect(() => {
    if (!captured || !bill || captured.nonce === lastCaptureNonce.current) return;
    lastCaptureNonce.current = captured.nonce;
    const { field, value } = captured;
    const coerced = coerceCapturedValue(field, value);
    const line = parseLineFieldKey(field);
    setDraft((prev) => {
      if (line) {
        const base = mergeBill(bill, prev);
        const items: BillLineItem[] = Array.isArray(base.line_items)
          ? (base.line_items as BillLineItem[]).slice()
          : [];
        while (items.length <= line.idx) {
          items.push({ description: '', quantity: null, unit_amount: null, line_total: null });
        }
        const cur = items[line.idx] ?? {};
        if (line.column === 'description') {
          items[line.idx] = { ...cur, description: coerced };
        } else {
          const n = coerced.trim() === '' ? null : Number(coerced);
          items[line.idx] = {
            ...cur,
            [line.column]: Number.isFinite(n as number) ? (n as number) : null,
          };
        }
        return { ...prev, line_items: items };
      }
      return { ...prev, [field]: coerced } as BillEditablePatch;
      // Each pick is its own undo step (coalesce:false) so ⌘Z reverts exactly
      // the value the operator just dropped in.
    }, `pick:${field}`, false);
  }, [captured, bill, setDraft]);

  useEffect(() => {
    if (!onAutoSave || !bill) return;
    if (draft === lastAutoSavedDraftRef.current) return;
    const draftKeys = Object.keys(draft);
    if (draftKeys.length === 0 && autoSavedKeysRef.current.size === 0) return;
    const timer = setTimeout(() => {
      const patch: BillEditablePatch = { ...draft };
      for (const key of autoSavedKeysRef.current) {
        if (!(key in patch)) {
          (patch as Record<string, unknown>)[key] =
            (bill as unknown as Record<string, unknown>)[key];
        }
      }
      lastAutoSavedDraftRef.current = draft;
      for (const k of draftKeys) autoSavedKeysRef.current.add(k);
      setAutoSaving(true);
      onAutoSave(patch)
        .catch(() => {
          // Failed PATCH (error surfaces via mutationError) — forget the
          // marker so the next draft change retries this state.
          lastAutoSavedDraftRef.current = null;
        })
        .finally(() => setAutoSaving(false));
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, bill, onAutoSave]);

  // Live draft preview for the document pane (PDF highlight regions follow
  // unsaved edits). Null while clean — see the prop doc for why.
  const isDraftDirty = Object.keys(draft).length > 0;
  useEffect(() => {
    if (!onDraftPreview) return;
    onDraftPreview(bill && isDraftDirty ? mergeBill(bill, draft) : null);
  }, [bill, draft, isDraftDirty, onDraftPreview]);

  // Keyboard undo/redo. ⌘Z / Ctrl+Z = undo; ⌘⇧Z / Ctrl+Y = redo. SCOPED to
  // this panel: we only act when focus is inside the rail (or nowhere, e.g.
  // right after a PDF pick leaves nothing focused) so the shortcut never
  // hijacks ⌘Z from an unrelated input elsewhere in the app. preventDefault is
  // required — controlled inputs otherwise swallow the keystroke into a dead
  // native undo, which is exactly the bug operators hit.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = (key === 'z' && e.shiftKey) || key === 'y';
      if (!isUndo && !isRedo) return;
      const root = asideRef.current;
      const active = document.activeElement;
      const inPanel = !!root && active instanceof Node && root.contains(active);
      const inert = !active || active === document.body;
      if (!inPanel && !inert) return;
      if (isRedo) {
        if (!canRedo) return;
        e.preventDefault();
        redoDraft();
      } else {
        if (!canUndo) return;
        e.preventDefault();
        undoDraft();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canUndo, canRedo, undoDraft, redoDraft]);

  const merged = useMemo(() => mergeBill(bill, draft), [bill, draft]);

  // True iff the bill already carries a non-zero extra charge
  // (either persisted on the server or in the user's draft). Used as
  // the "auto-reveal" trigger so a freshly-loaded bill with freight
  // doesn't need an interactive click to expose the row.
  // ``parseFloat`` tolerates DRF's stringified DecimalField; bad/NaN
  // values are treated as "no charge" (same as null) so a malformed
  // payload can never strand the UI in a half-rendered state.
  const hasExtraCharge = useMemo(() => {
    const raw = merged.extra_charge_amount;
    if (raw == null || raw === '') return false;
    const n = parseFloat(String(raw));
    return Number.isFinite(n) && n > 0;
  }, [merged.extra_charge_amount]);

  // Final visibility predicates — reveal the inputs when either the
  // user clicked "+ Add" OR the bill already has a value, but never
  // show the inviting "+ Add" affordance once the bill is in Xero
  // (re-pushing is gated upstream so the action would mislead).
  const showExtraChargeFields = hasExtraCharge || extraChargeRevealed;
  const showAddExtraChargeButton =
    !showExtraChargeFields && bill?.status !== 'pushed';

  if (loading && !bill) {
    return <BillFormSkeleton />;
  }
  if (!bill) {
    return <Empty>Select a bill from the list to view details.</Empty>;
  }

  const isDirty = Object.keys(draft).length > 0;
  // Don't let a background autosave disable the inputs mid-typing — the
  // form must stay editable while its own debounced PATCH is in flight.
  const inputsBusy = busy && !autoSaving;
  const status = bill.status;
  const canApprove = status === 'extracted' || status === 'reviewed';
  const canReject = status !== 'pushed';
  const canPush = status === 'reviewed' && !bill.xero_invoice_id;

  function setField<K extends keyof BillEditablePatch>(key: K, value: BillEditablePatch[K]) {
    // Coalesce consecutive edits to the SAME field into one undo step, so ⌘Z
    // reverts a whole typed value (or a cleared line-items grid) rather than
    // one keystroke at a time.
    setDraft((prev) => ({ ...prev, [key]: value }), `field:${String(key)}`, true);
  }

  // Link/unlink the bill to a canonical Supplier. Picking one also fills the
  // free-text vendor_name (and email when blank) so the displayed identity,
  // the Xero push, and the structured FK all agree; unlinking leaves
  // vendor_name as-is (the operator may still want the text).
  function handlePickSupplier(option: BillSupplierOption | null) {
    setDraft(
      (prev) => {
        const next: BillEditablePatch = { ...prev, supplier: option ? option.id : null };
        if (option) {
          next.vendor_name = option.name;
          const currentEmail = merged.vendor_email ?? '';
          if (!currentEmail && option.email) next.vendor_email = option.email;
        }
        return next;
      },
      'supplier',
      false,
    );
  }

  async function handleSave() {
    if (!isDirty) return;
    await onSave(draft);
    resetDraft({});
    // Explicit Save commits + re-baselines (the caller replaces the bill with
    // the response) — autosaved keys must not be re-patched from a stale base.
    autoSavedKeysRef.current = new Set();
    lastAutoSavedDraftRef.current = null;
  }

  // Ensure the bill has a PERSISTED linked supplier, creating one from the
  // extracted vendor when none exists. Returns true once the server holds a
  // linked supplier (so /supplier-term/ — which writes to the LINKED supplier
  // — can succeed). Operator ask: "if the supplier is not created it should
  // create the supplier and link it."
  async function ensureSupplierPersisted(): Promise<boolean> {
    setSupplierError(null);
    // Already persisted and the draft hasn't pointed somewhere else.
    if (bill?.supplier_detail?.id && draft.supplier === undefined) return true;

    let supplierId: string | null =
      draft.supplier !== undefined ? draft.supplier : (bill?.supplier ?? null);
    const patch: BillEditablePatch = { ...draft };

    if (!supplierId) {
      if (typeof client.createSupplier !== 'function') {
        setSupplierError('Supplier creation is not available in this app.');
        return false;
      }
      const name = (merged.vendor_name ?? '').trim();
      if (!name) {
        setSupplierError('Add a vendor name first so the supplier can be created.');
        return false;
      }
      try {
        const created = await client.createSupplier({
          name,
          email: merged.vendor_email,
          abn: merged.vendor_abn,
        });
        supplierId = created.id;
        patch.vendor_name = created.name;
      } catch (e) {
        setSupplierError(
          e instanceof Error ? e.message : 'Could not create the supplier. Please try again.',
        );
        return false;
      }
    }

    patch.supplier = supplierId;
    try {
      await onSave(patch);
      resetDraft({});
      autoSavedKeysRef.current = new Set();
      lastAutoSavedDraftRef.current = null;
      return true;
    } catch (e) {
      setSupplierError(
        e instanceof Error ? e.message : 'Could not link the supplier. Please try again.',
      );
      return false;
    }
  }

  // One-click create+link from the Vendor section.
  async function handleCreateAndLinkSupplier() {
    setSupplierBusy(true);
    try {
      await ensureSupplierPersisted();
    } finally {
      setSupplierBusy(false);
    }
  }

  // Apply a payment term from the chips. ``scope`` decides who it lands on:
  // 'supplier' writes the vendor's term (and re-dates all their unpaid
  // bills), 'bill' records a one-off exception. Supplier scope creates+links
  // the supplier first if needed; bill scope doesn't need one at all, since
  // an unlinked bill can still carry its own term. Either way the backend
  // re-derives this bill's due date.
  async function handleApplyTerm(
    termType: BillTermType | '',
    customDays?: number | null,
    scope: 'bill' | 'supplier' = 'supplier',
  ) {
    if (!onSetSupplierTerm) return;
    setSupplierBusy(true);
    try {
      if (termType === '') {
        const hasSupplier = Boolean(bill?.supplier_detail?.id || draft.supplier || bill?.supplier);
        // Clearing a bill-level exception is meaningful even unlinked; a
        // supplier-scope clear needs a supplier to clear it on.
        if (scope === 'supplier' && !hasSupplier) return;
        await onSetSupplierTerm({ term_type: '', scope });
        return;
      }
      if (scope === 'supplier') {
        const ok = await ensureSupplierPersisted();
        if (!ok) return;
      }
      await onSetSupplierTerm({
        term_type: termType,
        custom_days: customDays ?? undefined,
        scope,
      });
    } finally {
      setSupplierBusy(false);
    }
  }

  async function handleReject() {
    const reason = window.prompt('Reason for rejecting this bill?') || '';
    if (!reason.trim()) return;
    await onReject(reason.trim());
  }

  // Cleanly retract the Extra Charge row in one click. Setting both
  // fields to ``null`` / ``''`` produces a valid PATCH payload (the
  // backend's DecimalField is null=True, the CharField is blank=True
  // with default=''), and dropping ``extraChargeRevealed`` re-hides
  // the affordance until the user opts in again. The button is
  // already hidden for pushed bills, so this never fires post-push.
  function removeExtraCharge() {
    setDraft(
      (prev) => ({
        ...prev,
        extra_charge_amount: null,
        extra_charge_name: '',
      }),
      'extra_charge',
      false,
    );
    setExtraChargeRevealed(false);
  }

  return (
    <aside
      ref={asideRef}
      className="flex h-full min-h-0 w-full flex-col overflow-y-auto border-l border-border bg-muted/30"
    >
      <BillDataPanelHeader
        bill={bill}
        status={status}
        isDirty={isDirty}
        busy={busy}
        canUndo={canUndo}
        canRedo={canRedo}
        canApprove={canApprove}
        canReject={canReject}
        canPush={canPush}
        onSave={handleSave}
        onUndo={undoDraft}
        onRedo={redoDraft}
        onApprove={onApprove}
        onReject={handleReject}
        onPushToXero={onPushToXero}
        onReExtract={onReExtract}
        onReclassify={onReclassify}
      />

      <div
        ref={bodyRef}
        className="px-4 pb-4 pt-3"
        onFocusCapture={(e) => {
          const key = (e.target as HTMLElement).getAttribute?.('data-bill-field');
          if (key) onFieldSelect?.(key);
        }}
        // Arm the clicked field on the CLICK itself, not only on focus. In the
        // embedded bills tree, focusing an input did NOT reliably fire the arm
        // path (the operator's recurring "still cannot select" — they click the
        // field, click the PDF value, and nothing fills because the field was
        // never armed). Clicks ARE reliably delegated here (the crosshair's
        // onClick works), so arming on click guarantees: click a field input →
        // it's armed → the next PDF pick fills it; and a value already grabbed
        // off the document (value-first) drops in immediately. closest() only
        // matches when the input itself is clicked, so the crosshair button
        // (a sibling of the input) never double-fires this.
        onClickCapture={(e) => {
          const el = (e.target as HTMLElement).closest?.('[data-bill-field]');
          const key = el?.getAttribute('data-bill-field');
          if (key) onFieldSelect?.(key);
        }}
      >
        {showMutationError && (
          <div
            role="alert"
            className="mb-3 flex items-start justify-between gap-2 rounded-xl border border-destructive/40 bg-destructive/5 px-3.5 py-2.5 text-xs text-destructive shadow-sm"
          >
            <span className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{mutationError}</span>
            </span>
            <button
              type="button"
              onClick={() => setDismissedError(mutationError)}
              aria-label="Dismiss error"
              className="-mr-1 -mt-0.5 shrink-0 rounded px-1 text-sm leading-none text-destructive/70 transition hover:text-destructive"
            >
              {'\u00D7'}
            </button>
          </div>
        )}

        {/*
          Empty-extraction callout — fires only when the AI ran but
          came back without the fields a bill of this type needs.
          The doc-type-aware logic in ``needsManualReview`` keeps the
          banner from crying wolf on correctly-parsed statements
          (which intentionally have a null invoice_number).
        */}
        {needsManualReview(bill) && (
          <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900 shadow-sm dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <p className="font-medium">AI extraction came back empty.</p>
            <p className="mt-0.5 text-amber-800/90 dark:text-amber-200/80">
              This document looks like a {documentTypeLabel(bill).toLowerCase()} —
              click <strong>Re-extract</strong> to retry, fill in the{' '}
              {(() => {
                const t = getDocumentType(bill);
                return t === 'statement' || t === 'remittance'
                  ? 'dates / total'
                  : 'bill # / dates / total';
              })()}{' '}
              below, or click <strong>Reject</strong> if it's not a
              payable bill.
            </p>
          </div>
        )}

        <BillVendorSection
          merged={merged}
          supplierDetail={bill.supplier_detail}
          busy={inputsBusy}
          status={status}
          canCreateSupplier={typeof client.createSupplier === 'function'}
          supplierBusy={supplierBusy}
          supplierError={supplierError}
          onPickSupplier={handlePickSupplier}
          onCreateAndLink={handleCreateAndLinkSupplier}
          setField={setField}
        />

        <BillIdentitySection
          merged={merged}
          predictedDueDate={bill.predicted_due_date}
          setField={setField}
        />

        {onSetSupplierTerm && onMarkPaid && (
          <BillPaymentSection
            bill={bill}
            busy={busy || supplierBusy}
            onApplyTerm={handleApplyTerm}
            onMarkPaid={onMarkPaid}
            vendorName={merged.vendor_name ?? ''}
          />
        )}

        <BillAmountsSection
          merged={merged}
          busy={inputsBusy}
          billStatus={bill.status}
          showExtraChargeFields={showExtraChargeFields}
          showAddExtraChargeButton={showAddExtraChargeButton}
          onRemoveExtraCharge={removeExtraCharge}
          onRevealExtraCharge={() => setExtraChargeRevealed(true)}
          setField={setField}
        />

        <BillAllocationSection bill={bill} />

        <LineItemsSection
          items={(merged.line_items as BillLineItem[]) || []}
          onChange={(next) => setField('line_items', next)}
        />

        <BillLineAllocationGrid
          items={(merged.line_items as BillLineItem[]) || []}
          onChange={(next) => setField('line_items', next)}
          accounts={coaAccounts}
          accountsLoading={coaLoading}
          disabled={inputsBusy}
        />

        <BillJournalPreview bill={merged} accounts={coaAccounts} />

        {bill.extraction_confidence != null && (
          <p className="mt-2 text-[11px] text-muted-foreground">
            AI extraction confidence: {bill.extraction_confidence}
          </p>
        )}
      </div>

    </aside>
  );
}

// Public re-exports (preserve the panel's original API surface after the
// P4-1 split moved these classifiers into ./bill-document-type).
export { getDocumentType, documentTypeLabel } from './bill-document-type';
export type { DocumentType } from './bill-document-type';
